import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Button, DefinitionList, EmptyState, Grid, PageHeader, Panel, Skeleton, Stack, t, Text,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { OutsideScopeBanner, useDisplayScope } from '../../components/ScopePicker';
import { RefusalPage } from '../../components/Refusal';
import { EnvironmentBadge, ServerStateBadge, serverStateColumn } from '../../components/ServerState';
import { EnvironmentBadges, ObligationBadges } from '../../components/Inventory';
import { ContactsDialog } from '../../components/catalogue/ContactsDialog';
import { PullRequestNotice } from '../../components/catalogue/PullRequestNotice';
import { api, unwrap } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useEnvironmentName } from '../../lib/environments';
import { ListTable } from '../../components/ListTable';
import { useEnvironmentFilter } from '../../components/EnvironmentPicker';

type Client = maintenance.components['schemas']['ClientDetail'];
type ProjectRow = maintenance.components['schemas']['ProjectRow'];
type ServerRow = maintenance.components['schemas']['ClientServer'];

/**
 * Client page (SPEC-005, ADR-011), DSM « Record with tabs ». D115: the overview says what needs an action
 * beside the client's summary (figures, contacts); its projects and its servers are their own tabs.
 */
export function ClientPage() {
  const { client = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { enums } = useSession();
  const envName = useEnvironmentName();
  const scope = useDisplayScope();
  // D73, D114: a project's state follows the global environment filter.
  const env = useEnvironmentFilter();
  const tab = params.get('tab') ?? 'overview';
  const [editing, setEditing] = useState(false);
  const detail = useQuery({
    queryKey: ['client', client, scope],
    queryFn: async () => unwrap(await api.maintenance.GET('/clients/{client}', { params: { path: { client }, query: { scope } } })),
    refetchInterval: 30_000,
    retry: false,
  });

  const crumbs = [{ label: t('nav.clients'), href: '/clients' }, { label: detail.data?.title ?? client }];
  if (detail.error) return <Shell crumbs={crumbs}><RefusalPage error={detail.error} /></Shell>;
  const c = detail.data;
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  const subtitle = c
    ? [
        t('client.projects', { n: c.counts.projects }),
        t('client.dedicated', { n: c.counts.dedicatedServers }),
        ...c.obligations.map(o => label(enums.obligations, o)),
        t('client.team', { team: c.team }),
      ].join(' · ')
    : undefined;

  const header = (
    <PageHeader
      band
      eyebrow={t('page.kind.client')}
      title={c?.title ?? client}
      status={c?.state ? <ServerStateBadge state={c.state} /> : undefined}
      subtitle={subtitle}
      tabs={{
        label: t('client.tabs.label'),
        value: tab,
        onChange: id => setParams(id === 'overview' ? {} : { tab: id }),
        items: (c?.tabs ?? ['overview', 'projects', 'servers', 'settings']).map(id => ({ id, label: t(`client.tabs.${id}`) })),
      }}
    />
  );

  const contacts = (list: Client['contacts']) => (
    list.length === 0
      ? <Text variant="body-sm" tone="mute">{t('client.contacts.none')}</Text>
      : <DefinitionList items={list.map(x => ({ key: `${x.role}-${x.email}`, label: t(`client.contacts.roles.${x.role}`), value: `${x.name} · ${x.email}${x.mobile ? ` · ${x.mobile}` : ''}` }))} />
  );

  return (
    <Shell crumbs={crumbs} header={header}>
      {!c ? (
        <Skeleton lines={8} />
      ) : (
        <Stack gap={24}>
          <OutsideScopeBanner kind="client" outside={c.outsideDisplayScope} />
          {tab === 'overview' && (
            // D115: what needs an action beside its summary; its projects and servers are their own tabs.
            <Grid columns="2-1">
              <Panel title={t('application.summary.title')}>
                {(() => {
                  const items = c.projects.flatMap(p => (p.environments ?? [])
                    .filter(e => e.state === 'incident' || e.state === 'to_update')
                    .map(e => (
                      <Alert key={`${p.name}:${e.name}`} tone={e.state === 'incident' ? 'neg' : 'warn'} action={t('client.issues.openProject')}
                        onAction={() => navigate(`/projects/${encodeURIComponent(p.name)}?tab=applications`)}
                        title={t(`client.issues.${e.state}`, { project: p.title, environment: envName(e.name) })} />
                    )));
                  return items.length
                    ? <Stack gap={12}>{items}</Stack>
                    : <Alert tone="pos" title={t('application.summary.allGood')} action={t('client.issues.openProjects')} onAction={() => setParams({ tab: 'projects' })}>{t('client.issues.allGoodBody', { n: c.counts.projects })}</Alert>;
                })()}
              </Panel>
              <Panel title={t('application.properties')}>
                <DefinitionList
                  stacked
                  items={[
                    { label: t('project.settings.team'), value: c.team },
                    { label: t('projects.columns.obligations'), value: c.obligations.length ? <ObligationBadges obligations={c.obligations} /> : null },
                    { label: t('client.tiles.projects'), value: String(c.counts.projects) },
                    { label: t('client.tiles.dedicated'), value: String(c.counts.dedicatedServers) },
                    { label: t('client.tiles.shared'), value: String(c.counts.sharedServers) },
                    { label: t('client.tiles.openIssues'), value: String(c.counts.openIssues), hint: t('client.tiles.openIssuesNote') },
                    { label: t('client.contacts.title'), value: c.contacts.length
                      ? <Stack gap={4}>{c.contacts.map(x => <Text key={`${x.role}-${x.email}`} variant="body-sm">{`${t(`client.contacts.roles.${x.role}`)} · ${x.name} · ${x.email}${x.mobile ? ` · ${x.mobile}` : ''}`}</Text>)}</Stack>
                      : null },
                  ]}
                />
              </Panel>
            </Grid>
          )}
          {tab === 'projects' && (
            <Panel title={t('client.projectsPanel')} flush>
              {/* D120: the server searches, filters, sorts and pages; the state follows the global environment filter (D114). */}
              <ListTable<ProjectRow>
                label={t('client.projectsPanel')}
                queryKey={['client-projects', client, env]}
                fetch={async query => unwrap(await api.maintenance.GET('/clients/{client}/projects', { params: { path: { client }, query: { ...query, env } as never } }))}
                rowKey="name"
                defaultSort={{ key: 'state', dir: 'asc' }}
                search={t('client.projectsSearch')}
                facets={[
                  { key: 'state', label: t('list.facets.state'), valueLabel: v => (v === 'none' ? t('inventory.noProd') : enums.states.find(x => x.value === v)?.label ?? v) },
                  { key: 'environment', label: t('projects.columns.environments'), valueLabel: envName },
                  { key: 'criticality', label: t('project.settings.criticality'), valueLabel: v => label(enums.criticalities, v) },
                  { key: 'obligation', label: t('projects.columns.obligations'), valueLabel: v => label(enums.obligations, v) },
                ]}
                onRowClick={p => navigate(`/projects/${encodeURIComponent(p.name)}`)}
                columns={[
                  serverStateColumn<ProjectRow>(enums, p => ({ state: p.state }), { noProduction: true }),
                  { key: 'title', label: t('projects.columns.name'), grow: true },
                  { key: 'applications', label: t('projects.columns.applications'), type: 'number' as const, width: 120 },
                  { key: 'environments', label: t('projects.columns.environments'), width: 320, sortable: false, render: (p: ProjectRow) => <EnvironmentBadges environments={p.environments} /> },
                  { key: 'obligations', label: t('projects.columns.obligations'), width: 220, sortable: false, render: (p: ProjectRow) => <ObligationBadges obligations={p.obligations} /> },
                ]}
                empty={<EmptyState framed={false} icon="folder" title={t('client.noProject')} />}
              />
            </Panel>
          )}
          {tab === 'servers' && (
            // D120: one list, dedicated and shared servers told apart by the « Hébergement » filter.
            <Panel title={t('client.serversPanel')} subtitle={t('client.sharedSubtitle')} flush>
              <ListTable<ServerRow>
                label={t('client.serversPanel')}
                queryKey={['client-servers', client]}
                fetch={async query => unwrap(await api.maintenance.GET('/clients/{client}/servers', { params: { path: { client }, query: query as never } }))}
                rowKey="name"
                defaultSort={{ key: 'state', dir: 'asc' }}
                search={t('client.serversSearch')}
                facets={[
                  { key: 'state', label: t('list.facets.state'), valueLabel: v => enums.states.find(x => x.value === v)?.label ?? v },
                  { key: 'placement', label: t('client.servers.placement'), valueLabel: v => t(`list.placement.${v}`) },
                  { key: 'environment', label: t('client.servers.environment'), valueLabel: v => enums.environments.find(e => e.value === v)?.label ?? v },
                  { key: 'project', label: t('client.servers.projects') },
                ]}
                onRowClick={s => navigate(`/servers/${encodeURIComponent(s.name)}`)}
                columns={[
                  serverStateColumn<ServerRow>(enums, s => ({ state: s.state })),
                  { key: 'name', label: t('client.servers.name'), mono: true, width: 220 },
                  { key: 'environment', label: t('client.servers.environment'), width: 140, render: (s: ServerRow) => (s.environment ? <EnvironmentBadge environment={s.environment} /> : t('inventory.none')) },
                  { key: 'projects', label: t('client.servers.projects'), grow: true, sortable: false, render: (s: ServerRow) => s.projects.join(', ') },
                  { key: 'clients', label: t('client.servers.placement'), width: 220, sortable: false,
                    render: (s: ServerRow) => (s.clients >= 2
                      ? t('project.servers.shared', { n: s.clients })
                      : [t('project.servers.dedicated'), s.provider ? label(enums.providers, s.provider) : null, s.region].filter(Boolean).join(' · ')) },
                ]}
                empty={<EmptyState framed={false} icon="dns" title={t('client.noServer')} />}
              />
            </Panel>
          )}
          {tab === 'settings' && <PullRequestNotice pullRequest={c.pullRequests.contacts} />}
          {tab === 'settings' && (
            <Panel
              title={t('client.settings.title')}
              actions={c.actions.includes('client.update') ? <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>{t('client.settings.editContacts')}</Button> : undefined}
            >
              <Stack gap={16}>
                {contacts(c.contacts)}
                <DefinitionList items={[{ label: t('client.settings.vault'), value: c.vault, mono: true }]} emptyText={t('client.settings.noVault')} />
              </Stack>
            </Panel>
          )}
        </Stack>
      )}
      {c && editing && <ContactsDialog open onClose={() => setEditing(false)} client={{ name: c.name, title: c.title }} current={c.contacts as Parameters<typeof ContactsDialog>[0]['current']} />}
    </Shell>
  );
}
